import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  confirmText?: string;
  showCancel?: boolean;
  cancelText?: string;
  onConfirm?: () => void;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  confirmText = 'Acceptar',
  showCancel = false,
  cancelText = 'Cancel·lar',
  onConfirm
}: ModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  // Tancar modal amb tecla Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    
    // Quan s'obre el modal, evitar scroll del body
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Tancar modal quan es fa clic fora
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, onClose]);
  
  // Obtenir els colors segons el tipus de modal
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-500',
          iconColor: 'text-green-500',
          buttonColor: 'bg-green-600 hover:bg-green-700'
        };
      case 'error':
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-500',
          iconColor: 'text-red-500',
          buttonColor: 'bg-red-600 hover:bg-red-700'
        };
      case 'warning':
        return {
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-500',
          iconColor: 'text-amber-500',
          buttonColor: 'bg-amber-600 hover:bg-amber-700'
        };
      default: // info
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-500',
          iconColor: 'text-blue-500',
          buttonColor: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };
  
  const styles = getTypeStyles();

  // Renderitzar l'icona segons el tipus de modal
  const renderIcon = () => {
    switch (type) {
      case 'success':
        // Icona més gran i més destacada per confirmacions d'ordre
        if (title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) {
          return (
            <svg className={`h-10 w-10 ${styles.iconColor} animate-bounce`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        }
        return (
          <svg className={`h-6 w-6 ${styles.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className={`h-6 w-6 ${styles.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={`h-6 w-6 ${styles.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      default: // info
        return (
          <svg className={`h-6 w-6 ${styles.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if (!isOpen) return null;

  // Utilitzem createPortal per afegir el modal al final del DOM
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn">
      <div 
        ref={modalRef}
        className={`relative rounded-lg shadow-xl ${styles.bgColor} border-l-4 ${styles.borderColor} overflow-hidden transition-all transform 
          ${(title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) && type === 'success' 
            ? 'animate-scaleIn border-2 max-w-lg w-full' 
            : 'animate-slideIn max-w-md w-full'}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {renderIcon()}
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <h3 className={`text-lg font-medium text-gray-900 ${
                (title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) && type === 'success' ? 'text-xl' : ''
              }`}>{title}</h3>
              <div className="mt-2">
                {/* whitespace-pre-line: els missatges poden portar salts de
                    línia per separar l'explicació del detall tècnic. */}
                <p className={`whitespace-pre-line ${
                  (title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) && type === 'success'
                    ? 'text-base leading-relaxed'
                    : 'text-sm'
                } text-gray-600`}>{message}</p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
            {showCancel && (
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                onClick={onClose}
              >
                {cancelText}
              </button>
            )}              <button
              type="button"
              className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 ${styles.buttonColor}
                ${(title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) && type === 'success' 
                  ? 'px-6 py-2.5 text-base font-medium' 
                  : ''}`}
              onClick={() => {
                if (onConfirm) {
                  onConfirm();
                }
                onClose();
              }}
            >
              {(title.includes('confirmada') || title.includes('Confirmed') || title.includes('confirmado')) && type === 'success' 
                ? t('cart.continueShopping')
                : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}